/**
 * uiActions — bryggan som låter Alex (chatt OCH röst) styra dashboardens UI.
 *
 * Backend-verktyget navigate_ui emittar ett `ui_action`-event på SSE-hubben
 * (/events/stream). Här mappas den logiska vyn till korsets panel + undervy
 * och skickas vidare som window-CustomEvents som komponenterna lyssnar på:
 *   - 'scc:focus-pane'   { pane }                      → FocusNavigator
 *   - 'scc:subview'      { subview }                   → SubViewPane
 *   - 'scc:open-contact' { contactId, contactName, pipelineId } → CrmView (byter
 *     pipeline-flik) + PipelineBoard (öppnar kortet)
 *   - 'scc:open-customer' { customerId, tab }              → CustomerView
 *   - 'scc:select-pipeline' { pipelineId }                 → CrmView (byter flik)
 *   - 'scc:open-deliverable' { id }                        → ArchiveView (öppnar rapporten)
 */

import { connectEventStream } from './../api/system';

interface PaneTarget {
    pane: string;
    subview?: string;
}

/** Logisk vy (som Alex känner den) → panel i korset + ev. undervy */
const VIEW_TO_PANE: Record<string, PaneTarget> = {
    alex: { pane: 'alex' },
    crm: { pane: 'sales', subview: 'crm' },
    leads: { pane: 'sales', subview: 'leads' },
    sequences: { pane: 'sales', subview: 'sequences' },
    shadow: { pane: 'sales', subview: 'shadow' },
    customers: { pane: 'customers' },
    website: { pane: 'content', subview: 'website' },
    office: { pane: 'content', subview: 'office' },
    archive: { pane: 'content', subview: 'archive' },
    system: { pane: 'system', subview: 'system' },
    skills: { pane: 'system', subview: 'skills' },
};

export interface OpenContactDetail {
    contactId: string;
    contactName: string | null;
    /** Pipelinen kortet ligger i; CrmView byter flik dit innan kortet öppnas. */
    pipelineId?: string | null;
}

export interface OpenCustomerDetail {
    customerId: string;
    customerName: string | null;
    tab: string | null;
}

export interface UiNavigateData {
    view?: string;
    contact_id?: string | null;
    contact_name?: string | null;
    pipeline_id?: string | null;
    customer_id?: string | null;
    customer_name?: string | null;
    customer_tab?: string | null;
    /** Arkivet: öppna en färdig rapport direkt (utredningar Alex skrivit). */
    deliverable_id?: string | null;
}

interface UiActionData extends UiNavigateData {
    action?: string;
    /** action 'present': Alex egen genomgång, spelas upp av voice/presenter. */
    steps?: (UiNavigateData & { say: string })[];
    /** action 'note': ett svar som kom in efteråt (t.ex. färdig research på VPS:en). */
    text?: string;
    /** Det som ska LÄSAS UPP, när det skiljer sig från det som visas. */
    speech?: string;
    speak?: boolean;
    /** 'delegate' = svar på något du bett om. 'pulse' = Alex säger till självmant. */
    source?: string;
    kind?: string;
}

/** Navigera till en logisk vy (panel + ev. undervy). Används av SSE-bryggan och presentern. */
export function navigateToView(view: string): void {
    const target = VIEW_TO_PANE[view];
    if (!target) return;
    window.dispatchEvent(new CustomEvent('scc:focus-pane', { detail: { pane: target.pane } }));
    if (target.subview) {
        window.dispatchEvent(new CustomEvent('scc:subview', { detail: { subview: target.subview } }));
    }
}

/** Hoppa till ett kontaktkort i CRM:t (från t.ex. en todo eller loggrad). */
export function focusContact(contactId: string, contactName: string | null = null): void {
    navigateToView('crm');
    window.dispatchEvent(new CustomEvent<OpenContactDetail>('scc:open-contact', {
        detail: { contactId, contactName },
    }));
}

/** Utför ett upplöst skärmmål: vy, pipeline-flik, kontaktkort, kund + flik. */
export function applyUiNavigate(data: UiNavigateData): void {
    if (data.view) navigateToView(data.view);
    if (data.pipeline_id && !data.contact_id) {
        window.dispatchEvent(new CustomEvent<{ pipelineId: string }>('scc:select-pipeline', {
            detail: { pipelineId: data.pipeline_id },
        }));
    }
    if (data.contact_id) {
        window.dispatchEvent(new CustomEvent<OpenContactDetail>('scc:open-contact', {
            detail: {
                contactId: data.contact_id,
                contactName: data.contact_name ?? null,
                pipelineId: data.pipeline_id ?? null,
            },
        }));
    }
    if (data.deliverable_id) {
        window.dispatchEvent(new CustomEvent<{ id: string }>('scc:open-deliverable', {
            detail: { id: data.deliverable_id },
        }));
    }
    if (data.customer_id) {
        window.dispatchEvent(new CustomEvent<OpenCustomerDetail>('scc:open-customer', {
            detail: {
                customerId: data.customer_id,
                customerName: data.customer_name ?? null,
                tab: data.customer_tab ?? null,
            },
        }));
    }
}

function handleUiAction(data: UiActionData): void {
    if (data.action === 'note') {
        const text = typeof data.text === 'string' ? data.text.trim() : '';
        if (text) {
            window.dispatchEvent(new CustomEvent<{ text: string; speech: string; speak: boolean; source: string }>('scc:alex-note', {
                detail: {
                    text,
                    speech: (typeof data.speech === 'string' && data.speech.trim()) ? data.speech : text,
                    speak: data.speak !== false,
                    source: data.source ?? 'delegate',
                },
            }));
        }
        return;
    }
    if (data.action === 'present') {
        const steps = (data.steps ?? []).filter((s) => s && typeof s.say === 'string' && s.say.trim());
        if (steps.length) window.dispatchEvent(new CustomEvent('scc:present', { detail: { steps } }));
        return;
    }
    if (data.action !== 'navigate') return;
    applyUiNavigate(data);
}

/** Anslut SSE-strömmen. Returnerar cleanup. Återansluter automatiskt via EventSource. */
export function subscribeUiActions(): () => void {
    const es = connectEventStream(
        (event) => handleUiAction(event.data as UiActionData),
        { types: 'ui_action' },
    );
    return () => es.close();
}
